import React, { useEffect, useState, useRef, useCallback } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { GraftonChat } from "@/components/GraftonChat";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import GoalCascadePanel from "@/components/GoalCascadePanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Trash2, TrendingUp, TrendingDown, Save,
  Calculator, BarChart3, AlertTriangle, CheckCircle2,
  ChevronLeft, ChevronRight, Lock, Sparkles, Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];

const DEFAULT_ROWS = [
  { id: "obj_ca", label: "CA Prévisionnel", category: "revenus", editable: true },
  { id: "ca_realise", label: "CA Réalisé", category: "revenus", editable: false },
  { id: "loyer", label: "Loyer / Local", category: "charges_fixes", editable: true },
  { id: "salaires", label: "Salaires & Charges sociales", category: "charges_fixes", editable: true },
  { id: "assurances", label: "Assurances", category: "charges_fixes", editable: true },
  { id: "abonnements", label: "Abonnements & Logiciels", category: "charges_fixes", editable: true },
  { id: "comptabilite", label: "Comptabilité / Juridique", category: "charges_fixes", editable: true },
  { id: "telecom", label: "Télécom & Internet", category: "charges_fixes", editable: true },
  { id: "achats_marchandises", label: "Achats Marchandises", category: "charges_variables", editable: true },
  { id: "transport", label: "Transport / Livraison", category: "charges_variables", editable: true },
  { id: "marketing", label: "Marketing & Publicité", category: "charges_variables", editable: true },
  { id: "commissions", label: "Commissions commerciales", category: "charges_variables", editable: true },
  { id: "frais_bancaires", label: "Frais Bancaires", category: "charges_variables", editable: true },
  { id: "impots", label: "Impôts & Taxes", category: "fiscalite", editable: true },
  { id: "tva", label: "TVA à reverser", category: "fiscalite", editable: true },
  { id: "cotisations", label: "Cotisations Sociales", category: "fiscalite", editable: true },
];

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  revenus: { label: "Revenus & Objectifs", color: "text-emerald-500" },
  charges_fixes: { label: "Charges Fixes", color: "text-blue-500" },
  charges_variables: { label: "Charges Variables", color: "text-amber-500" },
  fiscalite: { label: "Fiscalité & Taxes", color: "text-violet-500" },
  custom: { label: "Lignes personnalisées", color: "text-muted-foreground" },
};

const CATEGORY_ORDER = ["revenus", "charges_fixes", "charges_variables", "fiscalite", "custom"];

interface TreasuryRow {
  id: string;
  label: string;
  category: string;
  editable: boolean;
  values: Record<string, number>;
}

const formatNum = (n: number) => {
  if (n === 0) return "";
  return new Intl.NumberFormat("fr-FR").format(n);
};

const formatCurrency = (n: number) => {
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)} M€`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(0)} k€`;
  return `${n.toLocaleString("fr-FR")} €`;
};

export default function TreasuryDashboard() {
  const [rows, setRows] = useState<TreasuryRow[]>([]);
  const [newRowLabel, setNewRowLabel] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [loaded, setLoaded] = useState(false);
  const [cascadeSynced, setCascadeSynced] = useState(false);
  const [qontoData, setQontoData] = useState<any>(null);
  const [qontoTx, setQontoTx] = useState<any[]>([]);
  const [qontoLoading, setQontoLoading] = useState(false);

  // Load Qonto data
  useEffect(() => {
    const loadQonto = async () => {
      setQontoLoading(true);
      try {
        const [balRes, txRes] = await Promise.all([
          fetch(`${API_URL}/api/qonto/balance`, { credentials: "include" as RequestCredentials }),
          fetch(`${API_URL}/api/qonto/transactions?per_page=20`, { credentials: "include" as RequestCredentials }),
        ]);
        if (balRes.ok) setQontoData(await balRes.json());
        if (txRes.ok) {
          const txData = await txRes.json();
          setQontoTx(txData.transactions || []);
        }
      } catch {}
      setQontoLoading(false);
    };
    loadQonto();
  }, []);

  // Load from backend
  useEffect(() => {
    const load = async () => {
      let loadedRows: TreasuryRow[] | null = null;
      try {
        const res = await fetch(`${API_URL}/api/treasury?fiscal_year=${fiscalYear}`, {
          credentials: "include" as RequestCredentials,
        });
        if (res.ok) {
          const { data } = await res.json();
          // Ensure saved rows match expected schema (id + category + values) — garbage rows are ignored.
          const savedRows = Array.isArray(data?.rows) ? data.rows : [];
          const valid = savedRows.filter(
            (r: { id?: string; category?: string; values?: Record<string, number> }) =>
              r && typeof r.id === "string" && typeof r.category === "string" && typeof r.values === "object"
          );
          if (valid.length > 0) loadedRows = valid;
        }
      } catch {}

      // If no saved data, start from defaults
      if (!loadedRows) {
        loadedRows = DEFAULT_ROWS.map((r) => ({
          ...r,
          values: Object.fromEntries(MONTHS.map((m) => [m, 0])),
        }));
      }

      // Auto-fill CA Prévisionnel from Goal Cascade if empty
      try {
        const gRes = await fetch(`${API_URL}/api/goals/current`, { credentials: "include" as RequestCredentials });
        if (gRes.ok) {
          const gData = await gRes.json();
          const monthly = gData?.cascade?.metrics?.monthly_forecast;
          const goalYear = gData?.goal?.start_date ? new Date(gData.goal.start_date).getFullYear() : fiscalYear;
          if (Array.isArray(monthly) && monthly.length > 0 && goalYear === fiscalYear) {
            const caRow = loadedRows.find((r) => r.id === "obj_ca");
            if (caRow) {
              const caIsEmpty = Object.values(caRow.values || {}).every((v) => !v);
              if (caIsEmpty) {
                const updated = { ...caRow.values };
                monthly.forEach((m: { month: string; target_eur: number }) => {
                  const mIdx = parseInt(m.month?.split("-")?.[1] || "0", 10) - 1;
                  if (mIdx >= 0 && mIdx < MONTHS.length) {
                    updated[MONTHS[mIdx]] = Math.round(m.target_eur);
                  }
                });
                caRow.values = updated;
                setCascadeSynced(true);
                // Persist immediately so the user doesn't lose the auto-fill
                setHasChanges(true);
              }
            }
          }
        }
      } catch {}

      setRows(loadedRows);
      setLoaded(true);
    };
    load();
  }, [fiscalYear]);

  const updateCell = useCallback((rowId: string, month: string, value: number) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, values: { ...r.values, [month]: value } } : r))
    );
    setHasChanges(true);
  }, []);

  const addCustomRow = () => {
    if (!newRowLabel.trim()) return;
    setRows((prev) => [
      ...prev,
      {
        id: `custom_${Date.now()}`,
        label: newRowLabel.trim(),
        category: "custom",
        editable: true,
        values: Object.fromEntries(MONTHS.map((m) => [m, 0])),
      },
    ]);
    setNewRowLabel("");
    setHasChanges(true);
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    setHasChanges(true);
  };

  const saveData = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/treasury`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials,
        body: JSON.stringify({ fiscal_year: fiscalYear, rows }),
      });
      if (res.ok) {
        setHasChanges(false);
        toast.success("Plan de trésorerie sauvegardé");
      } else {
        const txt = await res.text().catch(() => "");
        toast.error(`Erreur ${res.status} : ${txt.slice(0, 120) || "sauvegarde échouée"}`);
        console.error("Treasury save failed", res.status, txt);
      }
    } catch (e) {
      toast.error("Erreur réseau — ta saisie est conservée, réessaie");
      console.error("Treasury save network error", e);
    }
    setSaving(false);
  };

  // ─── Autosave : sauve automatiquement après 3s d'inactivité ──
  useEffect(() => {
    if (!loaded || !hasChanges || rows.length === 0) return;
    const t = setTimeout(() => {
      saveData();
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, hasChanges, loaded]);

  const getRowTotal = (row: TreasuryRow) =>
    MONTHS.reduce((sum, m) => sum + ((row.values || {})[m] || 0), 0);

  const getMonthRevenue = (month: string) =>
    (rows.find((r) => r.id === "obj_ca")?.values || {})[month] || 0;

  const getMonthRealise = (month: string) =>
    (rows.find((r) => r.id === "ca_realise")?.values || {})[month] || 0;

  const getMonthCharges = (month: string) =>
    rows
      .filter((r) => !["revenus"].includes(r.category))
      .reduce((sum, r) => sum + ((r.values || {})[month] || 0), 0);

  const getMonthResult = (month: string) => getMonthRevenue(month) - getMonthCharges(month);

  const getMonthEcart = (month: string) => {
    const realise = getMonthRealise(month);
    const previsionnel = getMonthRevenue(month);
    if (previsionnel === 0) return 0;
    return realise - previsionnel;
  };

  const getCumulativeResult = (monthIndex: number) => {
    let cumul = 0;
    for (let i = 0; i <= monthIndex; i++) cumul += getMonthResult(MONTHS[i]);
    return cumul;
  };

  // KPI calculations
  const totalPrev = MONTHS.reduce((s, m) => s + getMonthRevenue(m), 0);
  const totalRealise = MONTHS.reduce((s, m) => s + getMonthRealise(m), 0);
  const totalCharges = MONTHS.reduce((s, m) => s + getMonthCharges(m), 0);
  const totalResult = totalPrev - totalCharges;
  const seuilRentabilite = totalCharges;
  const monthsWithDeficit = MONTHS.filter((m) => getMonthResult(m) < 0);

  const groupedRows = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    ...CATEGORY_LABELS[cat],
    rows: rows.filter((r) => r.category === cat),
  })).filter((g) => g.rows.length > 0);

  // Build context for Grafton chat
  const tableContext = rows
    .map((r) => `${r.label}: ${MONTHS.map((m) => `${m}=${(r.values || {})[m] || 0}`).join(", ")} | Total=${getRowTotal(r)}`)
    .join("\n");
  const resultLine = MONTHS.map(
    (m, i) => `${m}: prév=${getMonthRevenue(m)}, réalisé=${getMonthRealise(m)}, charges=${getMonthCharges(m)}, résultat=${getMonthResult(m)}, cumul=${getCumulativeResult(i)}`
  ).join("\n");

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          {/* Hero Header */}
          <div className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/10">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.08),transparent_60%)]" />
            <div className="relative px-6 py-8 md:px-10 md:py-10">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Calculator className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h1 data-testid="treasury-title" className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                      Plan de Trésorerie
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setFiscalYear((y) => y - 1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Badge variant="outline" className="text-xs">
                        Année Fiscale {fiscalYear}
                      </Badge>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setFiscalYear((y) => y + 1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasChanges && (
                    <Badge variant="secondary" className="animate-pulse text-xs">
                      Modifications non sauvegardées
                    </Badge>
                  )}
                  <Button
                    onClick={saveData}
                    variant="hero"
                    size="sm"
                    disabled={saving}
                    className="gap-1.5"
                    data-testid="save-treasury-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? "Sauvegarde..." : hasChanges ? "Sauvegarder" : "Sauvegardé ✓"}
                  </Button>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6" data-testid="treasury-kpis">
                <div className="rounded-xl bg-card border border-border/50 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">CA Prévisionnel</p>
                  <p className="text-xl font-bold text-foreground mt-1">{formatCurrency(totalPrev)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Objectif annuel</p>
                </div>
                <div className="rounded-xl bg-card border border-border/50 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Solde Qonto</p>
                  {qontoData ? (
                    <>
                      <p className="text-xl font-bold text-emerald-500 mt-1">{formatCurrency(qontoData.total_balance)}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{qontoData.accounts?.length || 0} compte(s) bancaire(s)</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xl font-bold text-muted-foreground/40 mt-1 flex items-center gap-1.5">
                        {qontoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} —
                      </p>
                      <p className="text-[10px] text-primary mt-0.5">Chargement Qonto...</p>
                    </>
                  )}
                </div>
                <div className="rounded-xl bg-card border border-border/50 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Charges</p>
                  <p className="text-xl font-bold text-amber-500 mt-1">{formatCurrency(totalCharges)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Dépenses annuelles</p>
                </div>
                <div className="rounded-xl bg-card border border-border/50 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Résultat Net Prévu</p>
                  <p className={`text-xl font-bold mt-1 ${totalResult >= 0 ? "text-emerald-500" : "text-destructive"}`}>
                    {formatCurrency(totalResult)}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">CA - Charges</p>
                </div>
                <div className="rounded-xl bg-card border border-border/50 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Seuil de Rentabilité</p>
                  <p className="text-xl font-bold text-foreground mt-1">{formatCurrency(seuilRentabilite)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">CA minimum requis</p>
                </div>
              </div>

              {/* €1M Launch Goal — commercial launch worldwide end of April */}
              <div className="mt-5" data-testid="million-goal-card">
                <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 p-5 shadow-[0_0_24px_rgba(200,150,50,0.15)]">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-primary/20 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-primary/80 font-semibold">Objectif Mondial — Lancement Avril</p>
                        <h2 className="text-xl md:text-2xl font-bold text-foreground mt-0.5">Route vers 1 000 000 € de CA</h2>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">CA Réalisé</p>
                      <p className="text-2xl md:text-3xl font-bold text-primary" data-testid="million-goal-current">
                        {formatCurrency(totalRealise)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">/ 1,0 M€</p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  {(() => {
                    const goal = 1_000_000;
                    const pct = Math.min(100, Math.max(0, (totalRealise / goal) * 100));
                    const gap = Math.max(0, goal - totalRealise);
                    return (
                      <div className="mt-5">
                        <div className="relative h-3 w-full rounded-full bg-background/60 border border-border/50 overflow-hidden">
                          <div
                            data-testid="million-goal-progress"
                            className="h-full bg-gradient-to-r from-primary via-primary to-amber-400 transition-[width] duration-700 ease-out"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-2 text-[11px]">
                          <span className="font-semibold text-primary" data-testid="million-goal-pct">
                            {pct.toFixed(1)}% atteint
                          </span>
                          <span className="text-muted-foreground">
                            {gap > 0 ? `Reste ${formatCurrency(gap)} à générer` : "Objectif atteint — prochain palier 2 M€"}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Milestones */}
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {[
                      { v: 100_000, l: "100 k€" },
                      { v: 250_000, l: "250 k€" },
                      { v: 500_000, l: "500 k€" },
                      { v: 1_000_000, l: "1 M€" },
                    ].map((m) => {
                      const reached = totalRealise >= m.v;
                      return (
                        <div
                          key={m.v}
                          className={`rounded-lg border px-2 py-1.5 text-center transition-colors ${
                            reached
                              ? "border-primary/50 bg-primary/15 text-primary"
                              : "border-border/50 bg-background/40 text-muted-foreground"
                          }`}
                        >
                          <p className="text-[10px] uppercase tracking-wider">Palier</p>
                          <p className="text-sm font-bold">{m.l}</p>
                          {reached && <CheckCircle2 className="h-3 w-3 mx-auto mt-0.5" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Alerts */}
              {monthsWithDeficit.length > 0 && totalPrev > 0 && (
                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-destructive/5 border border-destructive/15" data-testid="deficit-alert">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-destructive">
                      Alerte Trésorerie : {monthsWithDeficit.length} mois en déficit prévisionnel
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Mois concernés : {monthsWithDeficit.join(", ")}. Grafton recommande d'ajuster les dépenses ou d'augmenter les objectifs CA.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Goal Cascade Engine — Grafton's strategic plan */}
          <div className="px-4 md:px-6 pt-6" data-testid="goal-cascade-section">
            <GoalCascadePanel />
          </div>

          {/* Qonto Transactions */}
          {qontoTx.length > 0 && (
            <div className="px-4 md:px-6 pt-6">
              <Card className="border-border/50 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-violet-500/10 flex items-center justify-center">
                      <BarChart3 className="h-4.5 w-4.5 text-violet-500" />
                    </div>
                    <div>
                      <CardTitle className="text-sm flex items-center gap-2">
                        Transactions Qonto
                        <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400">LIVE</Badge>
                      </CardTitle>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {qontoTx.length} dernières transactions — LBAL INVEST
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border/30 max-h-[300px] overflow-auto">
                    {qontoTx.map((tx, i) => (
                      <div key={tx.id || i} className="flex items-center gap-3 px-5 py-3 hover:bg-muted/10 transition-colors">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                          tx.side === "credit" ? "bg-emerald-500/10" : "bg-red-500/10"
                        }`}>
                          {tx.side === "credit" ? (
                            <TrendingUp className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{tx.label || "Transaction"}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {tx.settled_at ? new Date(tx.settled_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : ""} — {tx.operation_type}
                          </p>
                        </div>
                        <p className={`text-sm font-bold tabular-nums ${tx.side === "credit" ? "text-emerald-500" : "text-red-400"}`}>
                          {tx.side === "credit" ? "+" : "-"}{tx.amount?.toFixed(2)} €
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Table */}
          <div className="px-4 md:px-6 py-6">
            <Card className="border-border/50 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <TrendingUp className="h-4.5 w-4.5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-sm">Tableau Prévisionnel & Réalisé</CardTitle>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Saisissez vos objectifs et dépenses. Les cellules "CA Réalisé" seront alimentées par Qonto/CRM.
                      </p>
                      {cascadeSynced && (
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px]" data-testid="cascade-synced-banner">
                          <TrendingUp className="h-3 w-3" />
                          CA Prévisionnel pré-rempli automatiquement depuis ton objectif cascade Grafton
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-auto max-h-[calc(100vh-320px)]">
                  <table className="min-w-full w-max text-sm" data-testid="treasury-table">
                    <thead className="sticky top-0 bg-card z-30">
                      <tr className="border-b border-border/50">
                        <th className="sticky left-0 bg-card z-40 text-left py-3 px-3 text-muted-foreground font-semibold min-w-[220px] w-[220px] shadow-[2px_0_8px_-2px_rgba(0,0,0,0.08)]">
                          Poste
                        </th>
                        {MONTHS.map((m) => (
                          <th key={m} className="text-center py-3 px-1 text-muted-foreground font-semibold min-w-[72px] w-[72px] text-xs">
                            {m}
                          </th>
                        ))}
                        <th className="text-right py-3 px-3 font-bold text-primary min-w-[85px] w-[85px] text-xs">
                          TOTAL
                        </th>
                        <th className="w-7" />
                      </tr>
                    </thead>
                    <tbody>
                      {groupedRows.map((group) => (
                        <React.Fragment key={group.category}>
                          <tr>
                            <td
                              colSpan={MONTHS.length + 3}
                              className={`px-3 py-2 font-semibold text-xs bg-muted/20 border-y border-border/30 ${group.color}`}
                            >
                              {group.label}
                            </td>
                          </tr>
                          {group.rows.map((row) => {
                            const isLocked = row.id === "ca_realise";
                            return (
                              <tr
                                key={row.id}
                                className={`border-b border-border/20 hover:bg-muted/10 transition-colors group ${isLocked ? "bg-muted/5" : ""}`}
                                data-testid={`row-${row.id}`}
                              >
                                <td className="sticky left-0 bg-card z-20 py-1.5 px-3 text-xs font-medium text-foreground min-w-[220px] w-[220px] shadow-[2px_0_8px_-2px_rgba(0,0,0,0.08)]">
                                  <div className="flex items-center gap-1.5">
                                    {isLocked && <Lock className="h-3 w-3 text-muted-foreground/50" />}
                                    <span className={isLocked ? "text-muted-foreground/60" : ""}>{row.label}</span>
                                  </div>
                                </td>
                                {MONTHS.map((m) => (
                                  <td key={m} className="py-0.5 px-0.5">
                                    {isLocked ? (
                                      <div className="h-7 flex items-center justify-end px-1.5 text-xs text-muted-foreground/30 italic">
                                        —
                                      </div>
                                    ) : (
                                      <Input
                                        type="number"
                                        data-testid={`cell-${row.id}-${m}`}
                                        value={row.values[m] || ""}
                                        onChange={(e) => updateCell(row.id, m, parseFloat(e.target.value) || 0)}
                                        className="border-0 bg-transparent h-7 text-xs text-right px-1 py-0 focus-visible:ring-1 focus-visible:ring-primary/30 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="0"
                                      />
                                    )}
                                  </td>
                                ))}
                                <td className="text-right px-2 py-1 font-semibold text-foreground text-xs">
                                  {isLocked ? (
                                    <span className="text-muted-foreground/30">—</span>
                                  ) : (
                                    formatNum(getRowTotal(row))
                                  )}
                                </td>
                                <td className="px-0.5 py-1">
                                  {row.category === "custom" && (
                                    <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => removeRow(row.id)}>
                                      <Trash2 className="h-3 w-3 text-muted-foreground" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}

                      {/* Écart Prév vs Réalisé */}
                      <tr className="bg-blue-500/5 border-t-2 border-blue-500/20">
                          <td className="sticky left-0 bg-blue-500/5 z-10 px-3 py-2 font-bold text-blue-500 text-xs shadow-[2px_0_8px_-2px_rgba(0,0,0,0.08)]">
                            <span className="flex items-center gap-1.5">
                              <BarChart3 className="h-3 w-3" /> Écart Prév. / Réalisé
                            </span>
                          </td>
                          {MONTHS.map((m) => {
                            const ecart = getMonthEcart(m);
                            const realise = getMonthRealise(m);
                            return (
                              <td key={m} className={`text-right px-1.5 py-2 font-semibold text-xs ${realise === 0 ? "text-muted-foreground/30" : ecart >= 0 ? "text-emerald-500" : "text-destructive"}`}>
                                {realise === 0 ? "—" : formatNum(ecart)}
                              </td>
                            );
                          })}
                          <td />
                          <td />
                        </tr>

                      {/* Résultat Net */}
                        <tr className="bg-primary/5 border-t-2 border-primary/20" data-testid="row-result-net">
                          <td className="sticky left-0 bg-primary/5 z-10 px-3 py-2.5 font-bold text-primary text-xs shadow-[2px_0_8px_-2px_rgba(0,0,0,0.08)]">
                            <span className="flex items-center gap-1.5">
                              <TrendingUp className="h-3 w-3" /> Résultat Net Prévu
                            </span>
                          </td>
                          {MONTHS.map((m) => {
                            const result = getMonthResult(m);
                            return (
                              <td key={m} className={`text-right px-1.5 py-2.5 font-semibold text-xs ${result >= 0 ? "text-emerald-500" : "text-destructive"}`}>
                                {formatNum(result)}
                              </td>
                            );
                          })}
                          <td className="text-right px-2 py-2.5 font-bold text-primary text-xs">
                            {formatNum(totalResult)}
                          </td>
                          <td />
                        </tr>

                      {/* Trésorerie Cumulée */}
                        <tr className="bg-muted/10" data-testid="row-cumul">
                          <td className="sticky left-0 bg-muted/10 z-10 px-3 py-2.5 font-bold text-foreground text-xs shadow-[2px_0_8px_-2px_rgba(0,0,0,0.08)]">
                            <span className="flex items-center gap-1.5">
                              <BarChart3 className="h-3 w-3 text-muted-foreground" /> Tréso. Cumulée
                            </span>
                          </td>
                          {MONTHS.map((m, i) => {
                            const cumul = getCumulativeResult(i);
                            return (
                              <td key={m} className={`text-right px-1.5 py-2.5 font-semibold text-xs ${cumul >= 0 ? "text-emerald-500" : "text-destructive"}`}>
                                {formatNum(cumul)}
                              </td>
                            );
                          })}
                          <td />
                          <td />
                        </tr>
                    </tbody>
                  </table>
                </div>

                {/* Add custom row */}
                <div className="flex items-center gap-3 p-4 border-t border-border/30">
                  <Button variant="outline" size="sm" onClick={addCustomRow} disabled={!newRowLabel.trim()} className="shrink-0 text-xs gap-1.5">
                    <Plus className="h-3.5 w-3.5" /> Ajouter un poste
                  </Button>
                  <Input
                    data-testid="new-row-input"
                    value={newRowLabel}
                    onChange={(e) => setNewRowLabel(e.target.value)}
                    placeholder="Ex: Frais de déplacement, Formations..."
                    className="h-8 text-xs flex-1 border-0 bg-transparent"
                    onKeyDown={(e) => e.key === "Enter" && addCustomRow()}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Info */}
            <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/15">
              <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">Comment utiliser ce tableau ?</span>
                <br />
                Saisissez vos objectifs de CA prévisionnel et vos dépenses mois par mois.
                La ligne "CA Réalisé" sera automatiquement alimentée par votre compte Qonto et votre CRM une fois connectés.
                Grafton comparera chaque mois le prévisionnel au réalisé et déclenchera des alertes + actions correctives.
                Vous pouvez aussi demander à Grafton de vous aider à remplir le tableau poste par poste via le chat.
              </div>
            </div>
          </div>
        </main>
        <GraftonChat
          context={{
            page: "tresorerie",
            tableData: tableContext,
            monthlyResults: resultLine,
            totalPrevisionnel: totalPrev,
            totalCharges,
            totalResult,
            seuilRentabilite,
            monthsEnDeficit: monthsWithDeficit,
          }}
          subtitle="Stratège Financier"
          welcomeMessage={`Je suis votre directeur financier IA. Votre plan de trésorerie ${fiscalYear} est ouvert. Je peux analyser vos flux, identifier les risques et vous aider à remplir chaque poste. Que souhaitez-vous faire ?`}
          quickPrompts={[
            "Analyse mon plan de trésorerie",
            "Aide-moi à remplir les charges fixes",
            "Quels sont mes mois à risque ?",
            "Calcule mon seuil de rentabilité",
          ]}
        />
      </div>
    </SidebarProvider>
  );
}
